import React, { useMemo } from 'react';

import { Platform, Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import { PROFILE_ICON } from 'config/testID';

import {
    selectProfileAvatar,
    selectProfileParentalRating,
} from 'store/user/selectors';

import { isTabletOrWeb } from 'utils/responsiveness';
import { replaceXWithValue } from 'utils/strings';

import { setParentalRating } from 'screens/Profiles/AddProfile/utils';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import ProfileEditLogo from '../ProfileEditLogo';
import ProfileNameInput from '../ProfileNameInput';
import ProfileRowItem from '../ProfileRowItem';
import styles from './styles';
import { kidsRatingItemSubtitle, standardAgeRatingItemSubtitle } from './utils';

const {
    PROFILE: {
        PROFILES_TYPE_STANDARD,
        PROFILES_TYPE_KIDS,
        PROFILE_AGE_RATING_BUTTON,
        PROFILE_KIDS_AGE_BUTTON,
        PROFILE_KIDS_AGE_DESCRIPTION,
    },
    ACCESSIBILITY: {
        PROFILE: { ACCESSIBILITY_PROFILE_EDIT_IMAGE },
    },
} = LANGUAGE_KEYS;

const ADULT_AGE_RATING_ITEM = {
    id: PROFILE_AGE_RATING_BUTTON,
    title: PROFILE_AGE_RATING_BUTTON,
    subtitle: LANGUAGE_KEYS.PROFILE['PROFILE_AGE_RATING_18+'],
};

const KIDS_AGE_RATING_ITEM = {
    id: PROFILE_KIDS_AGE_BUTTON,
    title: PROFILE_KIDS_AGE_BUTTON,
    subtitle: PROFILE_KIDS_AGE_DESCRIPTION,
};

type Props = {
    text: string;
    profileNameErrorText: string;
    errorColor?: string;
    isChildModeEnabled: boolean;
    iconName?: string;
    onEndEditing: () => void;
    onChangeText: (text: string) => void;
    onPress: () => void;
    onAvatarPress: () => void;
};

function ProfileForm({
    text,
    profileNameErrorText,
    errorColor,
    isChildModeEnabled,
    iconName,
    onEndEditing,
    onChangeText,
    onPress,
    onAvatarPress,
}: Props) {
    const { t } = useTranslation();

    const profileParentalRating = useSelector(selectProfileParentalRating);
    const profileAvatar = useSelector(selectProfileAvatar);

    const AGE_RATING_ITEM = isChildModeEnabled
        ? KIDS_AGE_RATING_ITEM
        : ADULT_AGE_RATING_ITEM;

    const parentalRating = useMemo(
        () => setParentalRating(profileParentalRating, isChildModeEnabled),
        [profileParentalRating, isChildModeEnabled],
    );

    return (
        <View style={[isTabletOrWeb && gStyles.flexRow, styles.container]}>
            <View style={styles.profileItemContainer}>
                <Pressable
                    style={({ pressed }) => [
                        styles.profileLogoContainer,
                        {
                            opacity: pressed ? 0.5 : 1,
                        },
                    ]}
                    onPress={onAvatarPress}
                    testID={PROFILE_ICON}
                    accessibilityLabel={t(ACCESSIBILITY_PROFILE_EDIT_IMAGE)}
                    accessibilityRole={Platform.select({
                        web: 'imagebutton',
                        default: 'button',
                    })}
                    accessible
                >
                    <ProfileEditLogo
                        iconId={profileAvatar || iconName}
                        isEditable
                        withPlaceholder
                    />
                </Pressable>
                <Text
                    category={ETextCategory.body2}
                    color={ETextColor.neutral}
                    importantForAccessibility="no"
                    accessibilityElementsHidden
                    bold
                    center
                >
                    {t(
                        isChildModeEnabled
                            ? PROFILES_TYPE_KIDS
                            : PROFILES_TYPE_STANDARD,
                    )}
                </Text>
            </View>
            <View>
                <ProfileNameInput
                    text={text}
                    profileNameErrorText={profileNameErrorText}
                    errorColor={errorColor}
                    onEndEditing={onEndEditing}
                    onChangeText={onChangeText}
                />
                <ProfileRowItem
                    onPress={onPress}
                    title={t(AGE_RATING_ITEM.title)}
                    subtitle={
                        isChildModeEnabled
                            ? `${replaceXWithValue(
                                  t(PROFILE_KIDS_AGE_DESCRIPTION),
                                  t(
                                      kidsRatingItemSubtitle[
                                          parentalRating.rating
                                      ],
                                  ),
                              )}`
                            : t(
                                  standardAgeRatingItemSubtitle[
                                      parentalRating.rating
                                  ],
                              )
                    }
                />
            </View>
        </View>
    );
}

export default ProfileForm;
